import { useRouteError } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '@/lib/errorReporter';
import {
  isChunkLoadError as sharedIsChunkLoadError,
  canRetryChunkLoad,
  recoverFromChunkError,
  resetChunkReloadCount,
  purgeServiceWorker,
} from '@/lib/chunkLoadError';
import { getPrerenderHtml, restorePrerenderHead } from '@/lib/prerenderSnapshot';

// The shared matcher covers every engine's phrasing of a chunk-load failure.
// This boundary sits at the router root, where a stale-HTML hydration mismatch
// has the same cause (a session that predates the deploy) and the same cure, so
// it reloads on those too.
const isChunkLoadError = (error: unknown): boolean => {
  if (sharedIsChunkLoadError(error)) return true;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('<!DOCTYPE') ||
    msg.includes('is not valid JSON') ||
    msg.includes('Unexpected token') ||
    msg.includes('Minified React error #423') ||
    msg.includes('Minified React error #418') ||
    msg.includes('Minified React error #425') ||
    msg.includes('hydrat')
  );
};

export default function RootErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation('common');

  // A page that arrived with server-rendered content never trades it for an
  // error screen. When a deploy re-hashes every chunk, Google's renderer
  // re-fetches the whole graph and drops some fetches (2026-08-27: 93 locale
  // pages rendered as "§ ERROR"; Google indexed that DOM and began folding
  // locale pages into each other as duplicates — /fi/use-cases/parking got a
  // /pt-pt canonical). Restoring the captured HTML hands bots the exact
  // content the server sent, and hands humans a readable page whose links
  // still work as plain navigations. The error screen remains for pages with
  // nothing to fall back to (the SPA surfaces behind the .htaccess fallback).
  const prerenderHtml = getPrerenderHtml();

  const shouldAutoReload = !prerenderHtml && isChunkLoadError(error) && canRetryChunkLoad();

  useEffect(() => {
    // Skip reporting when we're about to auto-reload: the user recovers
    // silently and the digest stays focused on errors that actually broke
    // something. Report when the user is left on the fallback UI, and also on
    // every prerender restore — that path replaces the reload entirely, and
    // losing it would blind the digest to exactly the WRS failures this fix
    // exists for.
    if (error && !shouldAutoReload) {
      // A chunk failure the prerender absorbed is a different population from
      // an error screen (bots under a resource budget, mostly), so the digest
      // counts it under its own component.
      const isChunk = isChunkLoadError(error);
      reportError(error, {
        component: prerenderHtml && isChunk ? 'chunk-recovered' : 'RootErrorBoundary',
        isChunkLoad: isChunk,
        prerenderRestored: !!prerenderHtml,
      });
    }
  }, [error, shouldAutoReload, prerenderHtml]);

  useEffect(() => {
    if (prerenderHtml) {
      // No reload: the restored static page IS the recovery. But a poisoned
      // SW/cache entry would survive to the next navigation, so heal it in the
      // background at zero network cost.
      if (isChunkLoadError(error)) {
        void purgeServiceWorker();
      }
      // Helmet commits on a deferred frame and strips the server's head tags
      // (see restorePrerenderHead), so restore now and again two frames later.
      restorePrerenderHead();
      let cancelled = false;
      const frame =
        typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame
          : (cb: FrameRequestCallback) => window.setTimeout(() => cb(0), 16);
      frame(() => frame(() => { if (!cancelled) restorePrerenderHead(); }));
      return () => { cancelled = true; };
    }
    // A chunk-load failure after a deploy is usually a stale or poisoned
    // service-worker cache: it serves an old or HTML-for-asset response, so a
    // plain reload just re-serves the bad bytes. recoverFromChunkError nukes
    // the SW and every cache first, then reloads, and caps the attempts on a
    // budget shared with the inner ErrorBoundary and main.tsx.
    if (shouldAutoReload) {
      recoverFromChunkError(error);
    } else if (isChunkLoadError(error)) {
      // Budget spent and the user is looking at the fallback: clear it so a
      // later navigation (or a later deploy this session) can retry again.
      resetChunkReloadCount();
    }
  }, [error, shouldAutoReload, prerenderHtml]);

  if (prerenderHtml) {
    // display:contents keeps the wrapper out of layout, so the restored DOM
    // sits under #root exactly as the server delivered it. innerHTML-inserted
    // <script> tags stay inert, which is fine: JSON-LD is read from the DOM,
    // and the page is intentionally static from here on.
    return (
      <div
        style={{ display: 'contents' }}
        data-prerender-restored="true"
        dangerouslySetInnerHTML={{ __html: prerenderHtml }}
      />
    );
  }

  if (shouldAutoReload) {
    // Show nothing while reloading
    return null;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-graphite-900 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-3 block">§ ERROR</span>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{t('error_something_went_wrong')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
          {t('error_unexpected')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 text-white dark:text-neutral-900 text-xs font-mono font-semibold uppercase tracking-wide transition-colors"
        >
          {t('error_refresh_page')} &rarr;
        </button>
      </div>
    </div>
  );
}
